import axios from "axios";
import { message } from "antd";

const isProd = process.env.NODE_ENV === 'production';

// In dev  → "/api"  (handled by setupProxy.js, cookies work same-origin)
// In prod → full URL (backend origin + /api)
const baseURL = isProd
  ? `${process.env.REACT_APP_BACKEND_URL || 'https://api.messageos.io'}/api`
  : '/api';

const instance = axios.create({
  baseURL,

  // withCredentials sends httpOnly cookies.
  // In dev, requests go through setupProxy.js (same-origin), so cookies work automatically.
  // In prod, backend CORS allows credentials with specific origins.
  withCredentials: true,
});

/* ── Session Expiry Handler ──────────────────────────────────── */
let isSessionExpiredShown = false;

function handleSessionExpiry() {
  if (isSessionExpiredShown) return;
  isSessionExpiredShown = true;

  // Clear all session data immediately
  sessionStorage.clear();

  // Show a brief notification then redirect — no waiting for user to click
  message.warning({
    content: 'Your session has expired. Redirecting to login...',
    key: 'session-expired',
    duration: 2,
    onClose: () => {
      isSessionExpiredShown = false;
      window.location.replace('/login');
    },
  });

  // Hard redirect after 2 seconds regardless
  setTimeout(() => {
    window.location.replace('/login');
  }, 2000);
}

// Intercept responses and check custom statuscode
instance.interceptors.response.use(
  (response) => {

    // Assuming the response looks like: { statuscode: 401, message: "...", data: ... }
    if (response.data && response.data.statuscode === 401) {
      handleSessionExpiry();
    }
    return response;
  },
  (error) => {
    // 401 HTTP-level — session expired
    if (error.response?.status === 401) {
      handleSessionExpiry();
      return Promise.reject(error);
    }

    // 429 Too Many Requests — rate limiting
    if (error.response?.status === 429) {
      const retryAfter = error.response?.data?.retryAfter || 30;
      message.destroy('rate-limit');
      message.warning({
        content: `Too many requests. Please wait ${retryAfter} seconds before retrying.`,
        key: 'rate-limit',
        duration: 5,
      });
      return Promise.reject(error);
    }

    // Enterprise plan gate — dispatch a custom event that UI components can listen to
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'ENTERPRISE_PLAN_REQUIRED'
    ) {
      window.dispatchEvent(new CustomEvent('enterprise-plan-required', {
        detail: {
          message: error.response.data.error,
          upgradeUrl: error.response.data.upgradeUrl || '/dashboard/pricing',
        },
      }));
    }

    // Generic 403 — permission denied for sub-admins
    if (
      error.response?.status === 403 &&
      error.response?.data?.code !== 'ENTERPRISE_PLAN_REQUIRED'
    ) {
      window.dispatchEvent(new CustomEvent('permission-denied', {
        detail: {
          message: error.response?.data?.message || "You don't have permission for this action",
        },
      }));
    }

    return Promise.reject(error);
  }
);

export default instance;